<template>
  <div>
    <VDialog
        v-if="isDialogOpen"
        :value="isDialogOpen"
        data-test="datesSelectionDialog"
        width="360"
        persistent
        outlined
    >
      <component
          :is="currentDateSelectionComponent"
          :isRoundTrip="isRoundTrip"
          :outwardState="outwardState"
          :returnTime="returnState.time"
          :returnDate="returnState.date"
          :returnState="returnState"
          :outwardTime="outwardState.time"
          :outwardDate="outwardState.date"
          :holidays="holidays"
          :workingHoursZoneWeekDays="workingHoursZoneWeekDays"
          :defaultMaxDate="getDefaultMaxDate"
          :defaultMinDate="getDefaultMinDate"
          :minTimeBeforeBooking="minTimeBeforeBooking"
          :dateNow="dateNow"
          :timeZone="timeZone"
          :buttonTextClasses="buttonTextClasses"
          @changeStep="changeStep"
          @setOutwardDate="setOutwardDate"
          @setReturnDate="setReturnDate"
          @closeDialog="closeDialog"
      />
    </VDialog>
    <!-- todo: return when story for this option will be ready -->
    <!-- <VCol
      cols="8"
      class="pb-0"
    > -->
    <ValidationObserver 
      ref="datesSelection" 
      name="datesSelection"
    >
      <div
        :class="outwardDate ? 'pt-3' : ''"
        @click.stop="openDialog('OutwardDateSelection')"
      >
        <ValidationProvider
            v-slot="{ errors, ariaInput }"
            ref="outward"
            name="outward"
            mode="lazy"
            :rules="{ required: true }"
        >
          <VTextField
              v-bind="ariaInput"
              v-model="outwardDate"
              class="pb-6 pt-0 mt-0"
              data-test="outwardDateField"
              :disabled="isOutwardNow"
              :hide-details="!errors.length"
              :error="!!errors.length"
              :error-messages="$t(errors[0])"
              :label="$t('bookingForm.dateSelection.selectOutwardDatePlaceholder')"
              prepend-icon="mdi-calendar"
              readonly

          />
        </ValidationProvider>
      </div>
      <!-- todo: return when story for this option will be ready -->
      <!-- <VCol
        cols="1"
        class="pb-0"
      >
        <VCheckbox
          v-model="isOutwardNow"
          color="brandingPrimary"
        >
          <template #label>
            <span class="inline-checkbox-label body-1 d-block">Now</span>
          </template>
        </VCheckbox>
      </VCol> -->
      <div 
        v-if="isRoundTrip"
        @click.stop="openDialog('ReturnDateSelection')"
      >
        <ValidationProvider
            v-slot="{ errors, ariaInput }"
            ref="return"
            mode="lazy"
            name="return"
            :rules="{ required: true }"
        >
          <VTextField
              v-bind="ariaInput"
              v-model="returnDate"
              class="pb-6 pt-0 mt-0"
              data-test="returnDateField"
              :label="$t('bookingForm.dateSelection.selectReturnDatePlaceholder')"
              :hide-details="!errors.length"
              :error="!!errors.length"
              :error-messages="$t(errors[0])"
              prependIcon="mdi-calendar"
              readonly
              @click.stop="openDialog('ReturnDateSelection')"
          />
        </ValidationProvider>
      </div>
    </ValidationObserver>
  </div>
</template>
<script lang="ts">
export default {
  name: 'SDatesSelection',
};
</script>

<script setup lang="ts">
import { computed, ref } from 'vue';
import moment from 'moment';
import TripTypeEnum from '@slg/web-customer-shared/src/enum/TripTypeEnum';
import SConstants from '@slg/web-customer-shared/src/utils/constants';
import STimeConverter from '@slg/web-customer-shared/src/utils/timeConverter';
import { useRouteRef } from '../../../composables';
import OutwardDateSelection from './outwardDateSelection/OutwardDateSelection.vue';
import ReturnDateSelection from './returnDateSelection/ReturnDateSelection.vue';

const props = defineProps({
  outwardState: {
    type: Object,
    default: null,
  },
  returnState: {
    type: Object,
    default: null,
  },
  tripType: {
    type: String,
    default: null,
  },
  minTimeBeforeBooking: {
    type: Number,
    default: null,
  },
  maxNumberOfDaysForFutureBooking: {
    type: Number,
    default: null,
  },
  allowOnDemandPickUpTime: {
    type: Boolean,
    default: null,
  },
  workingHoursZoneWeekDays: {
    type: Array,
    default: () => [],
  },
  holidays: {
    type: Array,
    default: () => [],
  },
  timeZone: {
    type: String,
    default: null,
  },
  buttonTextClasses: {
    type: String,
    default: 'textWhite--text',
  },
});

const emit = defineEmits([
  'clearDates',
  'setOutwardDate',
  'trackGtmEventApplyDateAndTimeForOutward',
  'setReturnDate',
  'trackGtmEventApplyDateAndTimeForReturn',
]);

const route = useRouteRef();

const isDialogOpen = ref(false);
const currentComponent = ref(null);
const isOutwardNow = ref(false);
const dateNow = ref(null);

const dateSelectionComponents = {
  OutwardDateSelection,
  ReturnDateSelection,
};
const currentDateSelectionComponent = computed(() => dateSelectionComponents[currentComponent.value]);

const outwardDate = computed(() => {
  if (props.outwardState.date && props.outwardState.time) {
    return STimeConverter.convertToFullDateFormat(
      props.outwardState,
      route.value.params.lang
    );
  }
  return '';
});

const returnDate = computed(() => {
  if (props.returnState.date && props.returnState.time) {
    return STimeConverter.convertToFullDateFormat(
      props.returnState,
      route.value.params.lang
    );
  }
  return '';
});

const getDefaultMaxDate = computed(() => {
  const maxDate = dateNow.value.clone();
  return maxDate
    .add(props.maxNumberOfDaysForFutureBooking, 'days')
    .format(SConstants.TIME_FORMAT.YYYY_MM_DD);
});

const getDefaultMinDate = computed(() => dateNow.value.format(SConstants.TIME_FORMAT.YYYY_MM_DD));

const isRoundTrip = computed(() => props.tripType === TripTypeEnum.ROUND_TRIP);

dateNow.value = moment();
if (
  !Number.isNaN(
    STimeConverter.convertStringToMilliseconds(
      props.outwardState.date,
      props.outwardState.time
    )
  ) &&
  dateNow.value.valueOf() >=
    STimeConverter.convertStringToMilliseconds(
      props.outwardState.date,
      props.outwardState.time
    )
) {
  emit('clearDates');
}

function openDialog(name) {
  dateNow.value = moment();
  currentComponent.value = name;
  isDialogOpen.value = true;
}

function changeStep(name) {
  currentComponent.value = name;
}

function closeDialog() {
  isDialogOpen.value = false;
}

function setOutwardDate(outwardDateValue) {
  emit('setOutwardDate', outwardDateValue);
  emit('trackGtmEventApplyDateAndTimeForOutward', outwardDateValue);
}

function setReturnDate(returnDateValue) {
  emit('setReturnDate', returnDateValue);
  emit('trackGtmEventApplyDateAndTimeForReturn', returnDateValue);
}
</script>
<style lang="scss" scoped>
</style>
